<script lang="ts">
  import type { GetCartQuery } from '$gql/storefront/operations';
  import { formatCurrency } from '$lib/utils/currency';
  import { splitTitleAndCategory } from '$lib/utils/shopify/splitTitleAndCategory';
  import QuantityButtons from './quantity-buttons.svelte';
  import Icon from '$components/ui/icon/icon.svelte';
  import { t } from '$lib/i18n';
  import NotifyMe from '$components/notify-me.svelte';
  import { trimVariantId } from '$lib/utils/shopify/productsId';
  import { buildPath } from '$lib/utils/paths';
  import ShopifyImg from '$components/shopify-img.svelte';
  import {
    categoryCampaigns,
    freeGiftCampaign,
    getCampaignColors,
    tieredCampaign,
    tieredCampaignColors
  } from '$lib/stores/campaigns';
  import { cn } from '$lib/utils';
  import { scale } from 'svelte/transition';
  import { page } from '$app/stores';
  import { CART_DRAWER_ID, FREE_GIFT, PRE_ORDER_ATTRIBUTE } from '$lib/constants';
  import { drawer } from '$lib/stores/drawer';
  import { createEventDispatcher } from 'svelte';

  export let data: NonNullable<GetCartQuery['cart']>['lines']['nodes'][number];

  const { title, category } = splitTitleAndCategory({
    title: data.merchandise.product.title,
    titlesCategoriesExceptions: $page.data.titlesCategoriesExceptions
  });

  $: planPriceAdjustments = data.sellingPlanAllocation?.priceAdjustments;

  $: planName = data.sellingPlanAllocation?.sellingPlan?.name;

  $: planPrice =
    planPriceAdjustments && planPriceAdjustments.length > 0 && data.quantity
      ? +planPriceAdjustments[0].price.amount * data.quantity
      : null;

  // Shopify adds a "Title": "Default Title" variant if there are no variants, so we need to account for that
  const hasOnlyDefaultVariant =
    data.merchandise.product.options.length === 1 &&
    data.merchandise.product.options[0].values.length === 1;
  const variantId = trimVariantId(data.merchandise.id);
  // if the price was changed by cart scripts, we won't have compareAtPrice but we'll use cost.amountPerQuantity (which gives us the original price)
  $: amount = planPrice
    ? planPrice
    : (data.merchandise.compareAtPrice?.amount ?? data.cost.amountPerQuantity.amount ?? 0);

  $: compareAtPrice = data.quantity
    ? amount * data.quantity
    : (data.discountAllocations || [])[0]?.discountedAmount?.amount;

  $: isSubscription = data.merchandise.product.sellingPlanGroups.edges.length > 0;
  $: hasTieredSaleDiscount =
    $tieredCampaign && !$tieredCampaign.isProductExcluded(data.merchandise.product);
  $: hasCategorySaleDiscount =
    !!$categoryCampaigns &&
    $categoryCampaigns.some((campaign) => !campaign.isProductExcluded(data.merchandise.product)) &&
    !hasTieredSaleDiscount;

  $: categoryCampaign = $categoryCampaigns?.find(
    (campaign) => !campaign.isProductExcluded(data.merchandise.product)
  );

  $: affectedBySale =
    compareAtPrice &&
    compareAtPrice > data.cost.totalAmount.amount &&
    (hasTieredSaleDiscount || hasCategorySaleDiscount);

  $: tieredAltTextColor = $tieredCampaignColors.tieredAltTextColor;
  $: tieredAltBackgroundColor = $tieredCampaignColors.tieredAltBackgroundColor;

  let categoryAltTextColor: string | undefined = undefined;
  let categoryAltBackgroundColor: string | undefined = undefined;
  let isBlackCategoryCampaignColor: boolean;

  $: if (categoryCampaign?.campaign_color) {
    const categoryCampaignColors = getCampaignColors(categoryCampaign.campaign_color);
    isBlackCategoryCampaignColor = categoryCampaignColors.isBlackColor;
    categoryAltTextColor = categoryCampaignColors.altTextColor;
    categoryAltBackgroundColor = categoryCampaignColors.altBackgroundColor;
  }

  $: isBlackCampaignColor =
    (hasTieredSaleDiscount && $tieredCampaignColors.isBlackTieredColor) ||
    (!hasTieredSaleDiscount && isBlackCategoryCampaignColor);

  $: href = $buildPath({
    path: `/products/${data.merchandise.product.handle}`,
    searchQuery: `variant=${variantId}`
  });

  $: todaysFreeGifts = $freeGiftCampaign?.campaign_free_gifts
    ?.filter((p) => typeof p !== 'string')
    .map((p) => p.slug);
  $: isFreeGift =
    (todaysFreeGifts || []).includes(data.merchandise.product.handle) &&
    (data.attributes || [])?.some((item) => item.key === FREE_GIFT);

  // Outlines
  // Free gift
  $: freeGiftOutline = $freeGiftCampaign?.free_gift_cart_outline;
  $: showFreeGiftOutline = isFreeGift && !!freeGiftOutline;
  $: freeGiftCampaignColors = $freeGiftCampaign?.campaign_color
    ? getCampaignColors($freeGiftCampaign?.campaign_color)
    : null;
  // Generic
  $: genericOutlineCallout = $page.data.configuration.content.outline_callout_titles?.find(
    (callout) => {
      const tags = (callout.tags || '').split('\n');
      return tags?.some((tag) => data.merchandise.product.tags.includes(tag));
    }
  );
  $: showGenericOutline = genericOutlineCallout != null && genericOutlineCallout?.cart_card != '';
  $: genericOutlineCampaignColors = genericOutlineCallout?.colors
    ? getCampaignColors(genericOutlineCallout.colors)
    : null;
  //Applied (Free Gift or Generic)
  $: outlineText = showFreeGiftOutline ? freeGiftOutline : genericOutlineCallout?.cart_card;
  $: showOutline = showGenericOutline || showFreeGiftOutline;
  $: outlineCampaignColors = showFreeGiftOutline
    ? freeGiftCampaignColors
    : genericOutlineCampaignColors;

  $: isPreSale =
    data.attributes?.some((item) => item.key === PRE_ORDER_ATTRIBUTE) &&
    data.attributes?.some((item) => item.value);

  const dispatch = createEventDispatcher<{
    select: NonNullable<GetCartQuery['cart']>['lines']['nodes'][number];
  }>();
</script>

<article
  class={cn(
    'rounded-md bg-highlight',
    showOutline && 'border border-[--outlineBackgroudColor] bg-[--outlineBackgroudColor]',
    showOutline && outlineCampaignColors?.isBlackColor && 'border border-accent bg-accent'
  )}
  style:--categoryAltBackgroundColor={categoryAltBackgroundColor}
  style:--categoryAltTextColor={categoryAltTextColor}
  style:--tieredAltBackgroundColor={tieredAltBackgroundColor}
  style:--tieredAltTextColor={tieredAltTextColor}
  style:--outlineBackgroudColor={outlineCampaignColors?.altTextColor}
  style:--freeGiftAltTextColor={freeGiftCampaignColors?.altTextColor}
>
  {#if showOutline}
    <p class="py-1 px-1 text-center text-highlight text-xs font-medium">
      {outlineText}
    </p>
  {/if}
  <div class="bg-highlight p-1.5 rounded-md">
    <div class="flex gap-2">
      <div
        class="flex w-[68px] h-[68px] shrink-0 items-center overflow-hidden justify-center rounded-sm border bg-primary/2"
      >
        {#if data.merchandise.image?.url}
          <a {href} class="transition-all hover:opacity-80">
            <ShopifyImg
              src={data.merchandise.image.url}
              alt={data.merchandise.image.altText}
              widths={[96]}
              sizes="96px"
            />
          </a>
        {/if}
      </div>
      <div class="flex w-full flex-col">
        <div class="flex-1 flex justify-between">
          <a {href} class="max-w-fit block">
            <p class="text-sm font-semibold">{title}</p>
            <p class="text-sm text-primary/40">
              <span>{category}</span>
              {#if !hasOnlyDefaultVariant && data.merchandise.selectedOptions.length > 0}
                ·
                {#each data.merchandise.selectedOptions as option, i}
                  <span>
                    {option.value}
                    {#if i !== data.merchandise.selectedOptions.length - 1}
                      /
                    {/if}
                  </span>
                {/each}
              {/if}
              {#if planName}
                · <span>{planName}</span>
              {/if}
              {#if isPreSale}
                · <span>Pre-sale</span>
              {/if}
            </p>
          </a>

          {#if affectedBySale}
            <div
              transition:scale={{ duration: 200 }}
              role="status"
              class={cn(
                'text-xs h-fit text-highlight py-0.5 px-[5px] rounded-full font-semibold',
                hasTieredSaleDiscount && 'bg-[var(--tieredAltTextColor)]',
                !hasTieredSaleDiscount &&
                  hasCategorySaleDiscount &&
                  'bg-[var(--categoryAltTextColor)]',
                isBlackCampaignColor && 'bg-accent text-highlight',
                isFreeGift && 'bg-[var(--freeGiftAltTextColor)]',
                isFreeGift && freeGiftCampaignColors?.isBlackColor && 'bg-accent text-highlight'
              )}
            >
              {#if isFreeGift}
                <span role="alert">{$t('cart.free-gift')}</span>
              {:else}
                -{Math.round(((1 - +data.cost.totalAmount.amount / compareAtPrice) * 100) / 5) * 5}%
              {/if}
            </div>
          {/if}
        </div>

        {#if data.quantity > 0}
          <div class="mt-3 flex w-full items-center justify-between">
            {#if isFreeGift}
              <div class="flex gap-x-4 items-center">
                {#if todaysFreeGifts && todaysFreeGifts.length > 1}
                  <button
                    class={cn(
                      'inline-block w-fit text-sm text-[var(--freeGiftAltTextColor)] font-semibold',
                      freeGiftCampaignColors?.isBlackColor && 'text-accent'
                    )}
                    on:click={() =>
                      drawer.open(CART_DRAWER_ID, {
                        meta: {
                          showFreeGiftCards: true
                        }
                      })}
                  >
                    {$t('cart.change-gift')}
                  </button>
                {/if}

                {#if !hasOnlyDefaultVariant}
                  <button
                    class="inline-block relative w-fit text-sm font-semibold text-primary after:content-['.'] after:absolute after:-left-2 after:-top-1"
                    on:click={() => dispatch('select', data)}
                  >
                    {#if isSubscription}
                      {$t('cart.change-variant-var', {
                        variant: 'subscription'
                      })}
                    {:else}
                      {$t('cart.change-variant-var', {
                        variant: data.merchandise.product.options[0].name
                      })}
                    {/if}
                  </button>
                {/if}
              </div>
            {:else}
              <QuantityButtons
                itemId={data.merchandise.id}
                id={data.id}
                qty={data.quantity}
                product={data}
              />
            {/if}
            <a {href} class="self-end">
              <p class="text-sm font-semibold flex items-center gap-1">
                {#if compareAtPrice && compareAtPrice > data.cost.totalAmount.amount}
                  <span class="line-through opacity-40 tabular-nums">
                    {$formatCurrency(data.cost.totalAmount.currencyCode, compareAtPrice)}
                  </span>
                {/if}
                {#if Number(data.cost.totalAmount.amount) === 0}
                  <span>{$t('amount.free')}</span>
                {:else}
                  <span
                    class={cn(
                      'tabular-nums',
                      hasTieredSaleDiscount && affectedBySale && 'text-[var(--tieredAltTextColor)]',
                      hasCategorySaleDiscount &&
                        affectedBySale &&
                        'text-[var(--categoryAltTextColor)]',
                      isBlackCampaignColor && 'text-accent'
                    )}
                  >
                    {$formatCurrency(
                      data.cost.totalAmount.currencyCode,
                      data.cost.totalAmount.amount
                    )}
                  </span>
                {/if}
              </p>
            </a>
          </div>
        {:else}
          <div class="mt-3 flex items-center text-sm text-danger">
            <Icon icon="alert" />
            <span>{$t('cart.out-of-stock.item')}</span>
          </div>
        {/if}
      </div>
    </div>
    {#if data.quantity < 0}
      <NotifyMe variant={variantId} productId={data.merchandise.product.id} />
    {/if}
  </div>
</article>
